import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, Megaphone, Sparkles, AlertTriangle } from "lucide-react";

const ALLOWED = [
  "Telecom, retail, banking, food & beverage, electronics, tourism and hospitality brands",
  "Clear, factual headlines under 90 characters (extra copy goes in the sub-text)",
  "High-resolution images (Unsplash direct links or your own CDN)",
  "Mobile-first mp4 or webm video ads (autoplays muted in the banner)",
  "Destination URLs that use HTTPS and open on a working, published web page",
  "Kwacha prices, valid until dates, and any discount conditions stated up front",
];

const REJECTED = [
  "Adult, gambling, tobacco, illegal-drug or weapons content",
  "Political campaigning, election propaganda or divisive social messaging",
  "Health, medical or financial claims without a Zambian regulator's approval",
  "Cryptocurrency schemes promising guaranteed returns or airdrops",
  "Ads that impersonate other EasyStay hosts, agents or partners",
  "Broken links, parked domains, or destinations that redirect to app stores without prior notice",
  "All-caps headlines, click-bait, or misleading calls to action ('you won!', 'act now')",
  "Copyrighted imagery you do not own or license",
];

const TIPS = [
  { icon: Sparkles, title: "Lead with the offer", body: "Put the discount, price, or unique benefit inside the first six words of the headline." },
  { icon: CheckCircle2, title: "Match the accent colour", body: "Pick a colour that reads well with white text — dark and saturated hues perform best." },
  { icon: ShieldCheck, title: "Give us proof", body: "For any regulated claim (medical, financial, alcohol) attach a link to your Zambian license or ZICTA registration." },
];

export default function AdGuidelines() {
  const updated = "August 2026";
  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8" data-testid="ad-guidelines-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
        <Megaphone className="h-3.5 w-3.5" /> Advertiser policy
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        Ad content guidelines
      </h1>
      <p className="mt-2 text-sm text-slate-500">EasyStay Zambia · Last updated {updated}</p>
      <p className="mt-6 max-w-2xl text-slate-700">
        Every submission is manually reviewed within 24 hours. To speed up approval — and avoid a rejection email —
        please check that your ad fits the rules below before you click <b>Submit for review</b>.
      </p>

      {/* Allowed */}
      <section className="mt-10 rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <h2 className="font-display text-xl font-bold text-slate-900">What we approve</h2>
        </div>
        <ul className="mt-4 space-y-2">
          {ALLOWED.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* Rejected */}
      <section className="mt-8 rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <XCircle className="h-6 w-6 text-rose-600" />
          <h2 className="font-display text-xl font-bold text-slate-900">What we reject</h2>
        </div>
        <ul className="mt-4 space-y-2">
          {REJECTED.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-500" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* Tips */}
      <section className="mt-8 rounded-3xl bg-forest p-8 text-white">
        <h2 className="font-display text-xl font-bold">Tips that help you get approved faster</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {TIPS.map((t) => (
            <div key={t.title} className="rounded-2xl bg-white/10 p-4 backdrop-blur">
              <t.icon className="h-5 w-5 text-copper" />
              <div className="mt-2 text-sm font-bold">{t.title}</div>
              <p className="mt-1 text-xs text-white/80">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Warning + CTA */}
      <div className="mt-8 flex items-start gap-3 rounded-2xl bg-amber-50 p-5 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div>
          If your ad is rejected we&apos;ll email a specific reason so you can resubmit. Ads that clearly violate these
          guidelines twice may be blocked from further submissions.
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Questions? <a href="mailto:hello@easystayzm.com" className="text-copper hover:underline">hello@easystayzm.com</a></p>
        <Link to="/advertise" data-testid="guidelines-submit-cta" className="btn-primary text-sm">
          Submit an ad
        </Link>
      </div>
    </div>
  );
}
